import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { MdLightMode, MdDarkMode } from 'react-icons/md';
import { FaWhatsapp, FaInstagram, FaEnvelope, FaMapMarkerAlt, FaBars, FaTimes } from 'react-icons/fa';
import { useContent } from '../hooks/useContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { usePageMeta } from '../hooks/usePageMeta';
import Breadcrumbs from '../components/Breadcrumbs';

const WHATSAPP_URL = 'https://wa.me/5581988856777?text=Ol%C3%A1!%20Gostaria%20de%20agendar%20uma%20consulta.';

function LogoWatermark({ className = '', size = 320, opacity = 0.15 }) {
  const { darkMode } = useTheme();
  return (
    <img
      src={darkMode ? '/logo-dark.png' : '/logo-white.png'}
      alt=""
      aria-hidden="true"
      className={`pointer-events-none select-none ${className}`}
      style={{ width: size, height: size, opacity }}
    />
  );
}

function LogoDivider() {
  const { darkMode } = useTheme();
  return (
    <div className="flex items-center justify-center py-6 sm:py-10 px-5">
      <span className="flex-1 h-px bg-emerald-200/60 dark:bg-emerald-700/40" />
      <img
        src={darkMode ? '/logo-dark.png' : '/logo-white.png'}
        alt=""
        aria-hidden="true"
        className="mx-4 sm:mx-6 w-8 h-8 sm:w-10 sm:h-10 opacity-50 dark:opacity-40"
      />
      <span className="flex-1 h-px bg-emerald-200/60 dark:bg-emerald-700/40" />
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();
  const { content } = useContent('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    document.body.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  usePageMeta({
    title: 'Gabriel de Carli — Psicólogo Clínico em Recife | CRP 02/29417',
    description: 'Psicólogo clínico Gabriel de Carli (CRP 02/29417) em Recife. Atendimento humanizado presencial e online. Ansiedade, depressão, autoestima, luto e mais. Primeira sessão gratuita.',
    path: '/',
  });

  const { hero, features, services, contact } = content || {};
  const scrollRef = useScrollReveal();

  return (
    <div ref={scrollRef} className="min-h-screen font-sans bg-stone-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100 scroll-smooth">

      {/* ─── NAVIGATION ─── */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/90 dark:bg-gray-900/95 shadow-sm backdrop-blur-xl py-2'
          : 'bg-transparent py-5'
      }`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 group">
              <img
                src={darkMode ? '/logo-dark.png' : '/logo-white.png'}
                alt="Gabriel de Carli Psicólogo Clínico"
                className={`transition-all duration-300 ${isScrolled ? 'h-9 w-9' : 'h-11 w-11'}`}
              />
              <div>
                <span className={`block font-serif font-semibold transition-all duration-300 ${
                  isScrolled
                    ? 'text-lg text-emerald-800 dark:text-white'
                    : 'text-xl text-white'
                }`}>
                  Gabriel de Carli
                </span>
                <span className={`block text-[11px] tracking-widest uppercase transition-all duration-300 ${
                  isScrolled ? 'text-emerald-600/70 dark:text-emerald-400/70' : 'text-white/70'
                }`}>
                  Psicólogo Clínico
                </span>
              </div>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              {[
                { label: 'Início', action: () => navigate('/') },
                { label: 'Sobre', action: () => navigate('/sobre') },
              ].map(item => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 relative group ${
                    isScrolled
                      ? 'text-gray-600 dark:text-gray-300 hover:text-emerald-700 dark:hover:text-emerald-400'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 w-0 h-px transition-all duration-300 group-hover:w-full ${
                    isScrolled ? 'bg-emerald-600 dark:bg-emerald-400' : 'bg-white'
                  }`} />
                </button>
              ))}

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? 'bg-emerald-700 text-white hover:bg-emerald-800'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                Agendar Consulta
              </a>

              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors duration-300 ${
                  isScrolled
                    ? 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                    : 'text-white/80 hover:bg-white/10'
                }`}
                aria-label="Alternar tema"
              >
                {darkMode ? <MdLightMode className="w-5 h-5" /> : <MdDarkMode className="w-5 h-5" />}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <button onClick={toggleTheme} className={`p-2 rounded-full transition-colors ${
                isScrolled ? 'text-gray-600 dark:text-gray-300' : 'text-white'
              }`} aria-label="Alternar tema">
                {darkMode ? <MdLightMode className="w-5 h-5" /> : <MdDarkMode className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-md transition-colors ${
                  isScrolled ? 'text-gray-600 dark:text-gray-300' : 'text-white'
                }`}
                aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              >
                {isMenuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-56 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
            <div className={`rounded-xl p-4 space-y-2 ${
              isScrolled ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white/10 backdrop-blur-xl'
            }`}>
              {['Início', 'Sobre'].map(label => (
                <button
                  key={label}
                  onClick={() => { navigate(label === 'Início' ? '/' : '/sobre'); setIsMenuOpen(false); }}
                  className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isScrolled
                      ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-center px-4 py-2.5 bg-emerald-700 text-white rounded-lg text-sm font-medium hover:bg-emerald-800 transition-colors"
              >
                Agendar Consulta
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative min-h-[80vh] sm:min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 pt-20 sm:pt-0">
        {/* Logo watermark — large, centered behind content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <LogoWatermark size={600} opacity={0.18} className="dark:opacity-[0.10] max-w-[80vw] max-h-[80vw] sm:max-w-none sm:max-h-none" />
        </div>

        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 to-transparent dark:from-gray-950/60" />

        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 w-full py-8 sm:py-0">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Text */}
            <div className="text-center md:text-left order-2 md:order-1">
              <p className="hero-animate hero-animate-delay-1 text-emerald-200/80 dark:text-emerald-400/60 text-xs sm:text-sm tracking-[0.25em] uppercase mb-3 sm:mb-4 font-sans">
                CRP 02/29417
              </p>
              <h1 className="hero-animate hero-animate-delay-2 font-serif text-3xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.15] mb-4 sm:mb-6">
                {hero?.title || 'Seja Bem-vindo(a)!'}
              </h1>
              <p className="hero-animate hero-animate-delay-3 text-base sm:text-lg text-emerald-100/80 dark:text-gray-300/80 leading-relaxed mb-6 sm:mb-10 max-w-lg mx-auto md:mx-0 font-light">
                {hero?.subtitle || 'Atendimento humanizado e profissional'}
              </p>
              <div className="hero-animate hero-animate-delay-4 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white text-emerald-800 hover:bg-emerald-50
                           px-6 sm:px-7 py-3 sm:py-3.5 rounded-full text-sm font-semibold tracking-wide
                           transition-all duration-300 hover:shadow-lg hover:shadow-white/20"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  Agendar Consulta
                </a>
                <button
                  onClick={() => navigate('/sobre')}
                  className="inline-flex items-center justify-center px-6 sm:px-7 py-3 sm:py-3.5 rounded-full text-sm font-medium
                           text-white border border-white/30 hover:bg-white/10 transition-all duration-300"
                >
                  Conheça mais
                </button>
              </div>
            </div>

            {/* Profile Photo */}
            <div className="hero-animate flex justify-center md:justify-end order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-6 rounded-full bg-emerald-400/20 dark:bg-emerald-500/10 blur-2xl animate-pulse" />
                <img
                  src="/gabriel-profile.jpg"
                  alt="Gabriel de Carli - Psicólogo Clínico em Recife CRP 02/29417"
                  className="relative w-40 h-40 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full object-cover
                           border-4 border-white/20 shadow-2xl"
                />
                {/* Small logo accent near photo */}
                <img
                  src="/logo-white.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute -bottom-1 -right-1 w-10 h-10 sm:w-16 sm:h-16 opacity-50"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BREADCRUMBS ─── */}
      <Breadcrumbs items={[
        { label: 'Início', path: '/' },
      ]} />

      {/* ─── LOGO DIVIDER ─── */}
      <LogoDivider />

      {/* ─── FEATURES ─── */}
      <section className="py-10 sm:py-16 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
            {features?.map((feature, index) => (
              <div
                key={index}
                className={`reveal fade-up delay-${index * 100} relative group p-5 sm:p-8 bg-white dark:bg-gray-900 rounded-2xl
                         border border-emerald-100 dark:border-gray-800
                         hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-none
                         hover:border-emerald-200 dark:hover:border-emerald-800
                         transition-all duration-500`}
              >
                {/* Tiny logo accent */}
                <img
                  src={darkMode ? '/logo-dark.png' : '/logo-white.png'}
                  alt="" aria-hidden="true"
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 opacity-[0.18] group-hover:opacity-[0.30] transition-opacity duration-500"
                />
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-emerald-800 dark:text-emerald-300 mb-2 sm:mb-3 pr-8">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LOGO DIVIDER ─── */}
      <LogoDivider />

      {/* ─── FAQ ─── */}
      <section className="reveal fade-up py-10 sm:py-16 px-4 sm:px-8 relative overflow-hidden">
        {/* Background watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <LogoWatermark size={500} opacity={0.10} />
        </div>

        <div className="relative max-w-3xl mx-auto">
          <h2 className="reveal fade-up font-serif text-2xl sm:text-4xl font-semibold text-center text-emerald-800 dark:text-white mb-2 sm:mb-3">
            Dúvidas Frequentes
          </h2>
          <p className="reveal fade-up delay-100 text-center text-gray-500 dark:text-gray-400 mb-8 sm:mb-12 font-light text-sm sm:text-base">
            Tudo o que você precisa saber antes da sua primeira sessão
          </p>

          <div className="space-y-3 sm:space-y-4">
            {(services || []).map((faq, index) => (
              <details
                key={index}
                className={`reveal fade-up delay-${Math.min(index * 100, 400)} group bg-white dark:bg-gray-900 rounded-xl border border-emerald-100 dark:border-gray-800
                         hover:border-emerald-200 dark:hover:border-emerald-800
                         transition-all duration-300 overflow-hidden`}
              >
                <summary className="flex justify-between items-center cursor-pointer p-4 sm:p-6 text-gray-800 dark:text-white
                               font-medium select-none text-sm sm:text-base">
                  <span className="pr-4">{faq.title}</span>
                  <span className="shrink-0 w-8 h-8 rounded-full bg-emerald-50 dark:bg-gray-800 
                               flex items-center justify-center transition-transform duration-300 group-open:rotate-180">
                    <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600 dark:text-gray-400 leading-relaxed font-light border-t border-emerald-50 dark:border-gray-800 pt-3 sm:pt-4 text-sm sm:text-base">
                  {faq.description}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LOGO DIVIDER ─── */}
      <LogoDivider />

      {/* ─── CONTACT ─── */}
      <section className="py-12 sm:py-20 px-4 sm:px-8 relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto">
          <h2 className="reveal fade-up font-serif text-2xl sm:text-4xl font-semibold text-center text-emerald-800 dark:text-white mb-2 sm:mb-3">
            {contact?.title || 'Entre em Contato'}
          </h2>
          <p className="reveal fade-up delay-100 text-center text-gray-500 dark:text-gray-400 mb-8 sm:mb-14 font-light text-sm sm:text-base">
            Estou disponível para agendar sua primeira conversa
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
            {/* Contact Info */}
            <div className="reveal fade-left space-y-3 sm:space-y-5">
              {[
                { icon: FaMapMarkerAlt, value: contact?.address, href: contact?.address ? `https://maps.google.com/?q=${encodeURIComponent(contact.address)}` : null },
                { icon: FaEnvelope, value: contact?.email, href: contact?.email ? `mailto:${contact.email}` : null },
                { icon: FaWhatsapp, value: contact?.whatsapp, href: contact?.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/\D/g, '')}` : null },
                { icon: FaInstagram, value: contact?.instagram, href: contact?.instagram ? `https://instagram.com/${contact.instagram.replace('@', '')}` : null },
              ].filter(item => item.value).map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white dark:bg-gray-900
                           border border-emerald-100 dark:border-gray-800
                           hover:border-emerald-300 dark:hover:border-emerald-700
                           hover:shadow-md transition-all duration-300 group"
                >
                  <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/30 
                               flex items-center justify-center shrink-0
                               group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50 transition-colors">
                    <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 dark:text-emerald-400" />
                  </span>
                  <span className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm leading-relaxed">
                    {item.value}
                  </span>
                </a>
              ))}
            </div>

            {/* CTA Card */}
            <div className="reveal fade-right relative flex flex-col justify-center items-center p-6 sm:p-10 rounded-2xl
                         bg-gradient-to-br from-emerald-700 to-emerald-800 dark:from-emerald-900 dark:to-gray-900
                         text-white overflow-hidden">
              {/* Logo watermark in CTA */}
              <div className="absolute inset-0 flex items-center justify-center">
                <LogoWatermark size={300} opacity={0.20} className="brightness-200" />
              </div>
              <div className="relative text-center">
                <h3 className="font-serif text-xl sm:text-2xl font-semibold mb-2 sm:mb-3">
                  Pronto para começar?
                </h3>
                <p className="text-emerald-100/80 text-xs sm:text-sm leading-relaxed mb-6 sm:mb-8 max-w-xs mx-auto font-light">
                  A primeira sessão é gratuita. Vamos conversar sobre o que você precisa.
                </p>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-emerald-800 hover:bg-emerald-50
                           px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-xs sm:text-sm font-semibold tracking-wide
                           transition-all duration-300 hover:shadow-lg"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  Agendar pelo WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="reveal fade-in bg-emerald-900 dark:bg-gray-900 text-white py-10 sm:py-14 relative overflow-hidden">
        {/* Logo watermark in footer */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <LogoWatermark size={200} opacity={0.15} className="brightness-200" />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col items-center gap-6">
            <img src="/logo-white.png" alt="Gabriel de Carli Psicólogo Clínico" className="w-16 h-16 opacity-80" />
            <div className="text-center">
              <p className="font-serif text-lg font-medium">Gabriel de Carli</p>
              <p className="text-emerald-300/60 text-xs tracking-widest uppercase mt-1">Psicólogo Clínico — CRP 02/29417</p>
            </div>
            <div className="flex gap-4">
              {contact?.instagram && (
                <a href={`https://instagram.com/${contact.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 rounded-full bg-white/10 hover:bg-emerald-500/30 hover:scale-110 flex items-center justify-center transition-all duration-300">
                  <FaInstagram className="w-4 h-4" />
                </a>
              )}
              {contact?.whatsapp && (
                <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 rounded-full bg-white/10 hover:bg-emerald-500/30 hover:scale-110 flex items-center justify-center transition-all duration-300">
                  <FaWhatsapp className="w-4 h-4" />
                </a>
              )}
              {contact?.email && (
                <a href={`mailto:${contact.email}`}
                   className="w-10 h-10 rounded-full bg-white/10 hover:bg-emerald-500/30 hover:scale-110 flex items-center justify-center transition-all duration-300">
                  <FaEnvelope className="w-4 h-4" />
                </a>
              )}
            </div>
            <p className="text-emerald-300/40 text-xs mt-4">
              © {new Date().getFullYear()} Gabriel de Carli — Todos os direitos reservados
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
